import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { callQueries } from '../queries';
import { sessionScope, sessionRevision } from '@/features/auth/session-scope';
import { CallRecovery } from './CallRecovery';
import { useTranslation } from '@/lib/i18n/react';
import { useCallStore } from '../call-store';
import { useAuthStore } from '@/store/auth-store';
import { IncomingCallModal } from './IncomingCallModal';

const loadCallRoom = () => import('./CallRoom');
const CallRoom = React.lazy(() =>
  loadCallRoom().then((module) => ({ default: module.CallRoom })),
);

/**
 * App-wide call overlay — mounted once (in providers) so incoming calls and the
 * in-call room work from ANY route. Reads the ephemeral call state from
 * `useCallStore` (fed by the realtime manager): `incoming` → ringing prompt,
 * `active` → full-screen room. Promoting an answered call to `active` is what
 * swaps the prompt for the room.
 */
export function CallOverlay() {
  const incoming = useCallStore((s) => s.incoming);
  const active = useCallStore((s) => s.active);
  const clearActive = useCallStore((s) => s.clearActive);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const token = useAuthStore(s => s.token);
  const scope = useAuthStore(s => sessionScope(s));
  const revision = useAuthStore(() => sessionRevision());
  // A missed realtime event must not make incoming calls invisible until the
  // user opens /calls. This read-only snapshot never joins a microphone/room.
  const activeOptions = callQueries.active();
  useQuery({ ...activeOptions, enabled: !!token && !!currentUserId && activeOptions.enabled, refetchOnWindowFocus: true });

  // Start fetching the LiveKit room while the call is still ringing. This
  // keeps the realtime/ringing shell lightweight at startup without adding a
  // perceptible delay after the agent accepts.
  React.useEffect(() => {
    if (incoming || active) void loadCallRoom();
  }, [incoming, active]);

  return (
    <>
      {/* The room takes precedence: while in a call, don't also show a ringing
          prompt for that same call. */}
      {active ? (
        <React.Suspense fallback={<CallRoomFallback />}>
          <CallRoom
            key={scope + ':' + revision + ':' + active.id}
            call={active}
            currentUserId={currentUserId}
            onLeave={clearActive}
          />
        </React.Suspense>
      ) : (
        incoming && (
          <IncomingCallModal
            key={scope + ':' + revision + ':' + incoming.id}
            call={incoming}
            currentUserId={currentUserId}
          />
        )
      )}
      <CallRecovery />
    </>
  );
}

function CallRoomFallback() {
  const { t } = useTranslation('calls');
  return (
    <div className="fixed inset-0 z-[200] grid place-items-center bg-ink-900 text-white">
      <div className="flex items-center gap-3 text-sm font-semibold text-white/75">
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-blaze-orange border-t-transparent" />
        {t('preparing')}
      </div>
    </div>
  );
}
